import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PublicShell from './PublicShell';
import Icon from '../../comps/Icon';
import publicBusinessSvc from '../../services/publicBusiness';
import { useI18n, formatDate } from '../../i18n/I18nContext';
import '../../style/css/public2026.css';

// Customer-facing translated label + chip modifier per liveStatus.
function statusChip(liveStatus, t) {
    switch (liveStatus) {
        case 'arrived': return { label: t('my.status_arrived'), cls: 'mr-chip--ok' };
        case 'seated': return { label: t('my.status_seated'), cls: 'mr-chip--ok' };
        case 'booked':
        default: return { label: t('my.status_confirmed'), cls: 'mr-chip--ok' };
    }
}

/*
 * MyReservations — the PUBLIC (no-auth) customer self-service portal at /my. A guest who booked
 * BY PHONE (no account) logs in with phone + a one-time SMS code to VIEW & CANCEL their own
 * future reservations. Two steps mirror the AuthPage OTP UX: (1) phone → send code, (2) code →
 * view list. SECURITY: the phone is the sole scope; the server proves it via a single-use,
 * 10-min, 5-attempt-locked SMS code and never exposes any other phone's data. Anti-enumeration:
 * step 1 always succeeds regardless of whether the phone has reservations.
 */
export default function MyReservations() {
    const { t, dateLocale } = useI18n();
    const [step, setStep] = useState('phone'); // 'phone' | 'code' | 'list'
    const [phone, setPhone] = useState('');
    const [otpCode, setOtpCode] = useState('');
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [cancellingToken, setCancellingToken] = useState('');
    const codeRef = useRef(null);

    useEffect(() => { if (step === 'code' && codeRef.current) codeRef.current.focus(); }, [step]);

    // Step 1 — send the SMS code. Anti-enumeration: always resolves ok on a good round-trip.
    const sendCode = useCallback(async () => {
        if (busy) return;
        if (!(/^0\d{8,9}$/.test(phone) || /^\+[1-9]\d{6,14}$/.test(phone))) { setError(t('my.phone_invalid')); return; }
        setBusy(true); setError('');
        const out = await publicBusinessSvc.sendCustomerOtp(phone);
        setBusy(false);
        if (out.ok) { setStep('code'); setOtpCode(''); }
        else { setError(t('my.send_failed')); }
    }, [busy, phone, t]);

    // Step 2 — verify the code and load the list. 313 → wrong/expired code.
    const verifyCode = useCallback(async () => {
        if (busy) return;
        if (!/^\d{4,6}$/.test(otpCode)) { setError(t('my.code_missing')); return; }
        setBusy(true); setError('');
        const out = await publicBusinessSvc.getCustomerReservations(phone, otpCode);
        setBusy(false);
        if (out.ok) { setRows(out.rows); setStep('list'); }
        else { setError(t('my.code_invalid')); }
    }, [busy, otpCode, phone, t]);

    // Auto-submit once a full 6-digit code is entered (mirrors AuthPage UX).
    useEffect(() => {
        if (step === 'code' && !busy && otpCode.length === 6) { verifyCode(); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [otpCode]);

    // Cancel one reservation by its secret manage token, then refresh the list.
    const doCancel = useCallback(async (manageToken) => {
        if (!manageToken || cancellingToken) return;
        // eslint-disable-next-line no-restricted-globals, no-alert
        if (!window.confirm(t('my.confirm_cancel'))) return;
        setCancellingToken(manageToken); setError('');
        const out = await publicBusinessSvc.cancelReservationByToken(manageToken);
        if (out.ok) {
            // Re-verify to get the fresh list — but the code is single-use, so just drop the row
            // locally (the server already cancelled it).
            setRows((prev) => prev.filter((r) => r.manageToken !== manageToken));
        } else {
            setError(t('my.cancel_failed'));
        }
        setCancellingToken('');
    }, [cancellingToken, t]);

    // ── STEP 1: phone ─────────────────────────────────────────────────────────
    if (step === 'phone') {
        return (
            <PublicShell>
                <div className="mr-card">
                    <h2 className="mr-title">{t('my.title')}</h2>
                    <p className="mr-sub">{t('my.phone_sub')}</p>
                    <form onSubmit={(e) => { e.preventDefault(); sendCode(); }}>
                        <input
                            className="my-input"
                            type="tel"
                            inputMode="numeric"
                            autoComplete="tel"
                            placeholder={t('my.phone_placeholder')}
                            value={phone}
                            onChange={(e) => setPhone(e.target.value.replace(/[^\d+]/g, '').slice(0, 16))}
                            dir="ltr"
                            aria-label={t('my.phone_aria')}
                        />
                        {error && <p className="mr-err">{error}</p>}
                        <button type="submit" className="pb-btn-primary" disabled={busy}>
                            {busy ? t('my.sending') : t('my.send_code')}
                        </button>
                    </form>
                </div>
            </PublicShell>
        );
    }

    // ── STEP 2: code ──────────────────────────────────────────────────────────
    if (step === 'code') {
        return (
            <PublicShell>
                <div className="mr-card">
                    <h2 className="mr-title">{t('my.code_title')}</h2>
                    <p className="mr-sub">{t('my.code_sub', { phone })}</p>
                    <form onSubmit={(e) => { e.preventDefault(); verifyCode(); }}>
                        <input
                            ref={codeRef}
                            className="my-input my-input--code"
                            type="text"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            placeholder={t('my.code_placeholder')}
                            value={otpCode}
                            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            dir="ltr"
                            aria-label={t('my.code_aria')}
                        />
                        {error && <p className="mr-err">{error}</p>}
                        <button type="submit" className="pb-btn-primary" disabled={busy}>
                            {busy ? t('my.checking') : t('my.view_reservations')}
                        </button>
                        <button
                            type="button"
                            className="pb-btn-ghost"
                            onClick={() => { setStep('phone'); setOtpCode(''); setError(''); }}
                            disabled={busy}
                        >
                            {t('my.change_number')}
                        </button>
                    </form>
                </div>
            </PublicShell>
        );
    }

    // ── STEP 3: list ──────────────────────────────────────────────────────────
    return (
        <PublicShell>
            <div className="my-list-wrap">
                <h2 className="mr-title my-list-title">{t('my.future_title')}</h2>
                {error && <p className="mr-err" style={{ textAlign: 'center' }}>{error}</p>}

                {rows.length === 0 ? (
                    <div className="mr-card mr-card--msg">
                        <p className="mr-sub" style={{ margin: 0 }}>{t('my.none')}</p>
                        <Link to="/discover" className="pb-btn-ghost pb-btn-link">{t('my.discover')}</Link>
                    </div>
                ) : (
                    rows.map((r) => {
                        const chip = statusChip(r.liveStatus, t);
                        const canCancel = r.liveStatus === 'booked' || r.liveStatus === 'arrived';
                        return (
                            <div className="my-res-card" key={r.manageToken || `${r.date}-${r.startTime}`}>
                                <div className="my-res-head">
                                    <span className="mr-biz">{r.businessName || t('my.default_biz')}</span>
                                    <span className={`mr-chip ${chip.cls} my-chip`}>{chip.label}</span>
                                </div>
                                <div className="mr-details my-res-details">
                                    <div className="mr-row"><span className="mr-ico" aria-hidden="true"><Icon name="calendar" size={18} /></span><span>{formatDate(r.date, dateLocale)}</span></div>
                                    <div className="mr-row"><span className="mr-ico" aria-hidden="true"><Icon name="clock" size={18} /></span><span>{String(r.startTime).slice(0, 5)}</span></div>
                                    <div className="mr-row"><span className="mr-ico" aria-hidden="true"><Icon name="users" size={18} /></span><span>{r.numberOfGuests} {t('my.diners')}</span></div>
                                    {r.guestName && <div className="mr-row"><span className="mr-ico" aria-hidden="true"><Icon name="customer" size={18} /></span><span>{r.guestName}</span></div>}
                                </div>
                                <div className="my-res-actions">
                                    {canCancel && (
                                        <button
                                            type="button"
                                            className="mr-cancel-btn my-cancel"
                                            onClick={() => doCancel(r.manageToken)}
                                            disabled={cancellingToken === r.manageToken}
                                        >
                                            {cancellingToken === r.manageToken ? t('my.cancelling') : t('my.cancel')}
                                        </button>
                                    )}
                                    {r.manageToken && (
                                        <Link to={`/r/${r.manageToken}`} className="pb-btn-ghost pb-btn-link my-open">{t('my.details')}</Link>
                                    )}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </PublicShell>
    );
}
